/**
 * @file ChequeDetail.js
 * @description Page component for displaying check transaction details, history, images lightbox, and status modifications.
 */

import '../css/pages/ChequeDetail.css';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getCheque, modifyCheque } from '../api/services';
import { imgUrl } from '../utils/imgUrl';
import DateInput from '../components/DateInput';
import toast from 'react-hot-toast';
import { formatDate, formatDateLong, formatDateShort, formatDateTime } from '../utils/formatDate';

/**
 * Formats numeric amount as US currency string.
 * @param {number|string} n - Amount.
 * @returns {string} Formatted currency.
 */
const fmt = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

const statusColor = { Cashed: 'badge-green', Pending: 'badge-yellow', Returned: 'badge-red', Void: 'badge-gray', Hold: 'badge-blue' };

/**
 * Modal lightbox for viewing front and back check images full screen.
 * @component
 * @param {Object} props
 * @param {Object} props.cheque - Cheque object containing checkImages.
 * @param {Function} props.onClose - Modal close handler.
 */
function ChequeImageModal({ cheque, onClose }) {
  const [side, setSide] = useState(cheque?.checkImages?.front ? 'front' : 'back');
  const hasFront = Boolean(cheque?.checkImages?.front);
  const hasBack  = Boolean(cheque?.checkImages?.back);

  if (!cheque) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 12, padding: '20px 22px',
          maxWidth: '92vw', maxHeight: '94vh',
          display: 'flex', flexDirection: 'column', gap: 14,
          boxShadow: '0 12px 50px rgba(0,0,0,0.55)',
          minWidth: 340,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>
              Check #{cheque.checkNumber}{' '}
              <span style={{ color: side === 'front' ? '#1a7a4a' : '#495057', fontWeight: 600, textTransform: 'capitalize' }}>
                — {side}
              </span>
            </div>
            <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>
              {formatDateShort(cheque.dateCashed)}
              {' · '}{fmt(cheque.amount)}
              {cheque.maker ? ` · ${cheque.maker}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {hasFront && (
              <button
                onClick={() => setSide('front')}
                style={{
                  padding: '5px 14px', fontSize: 13, borderRadius: 5, cursor: 'pointer',
                  border: '1.5px solid #1a7a4a',
                  background: side === 'front' ? '#1a7a4a' : 'white',
                  color: side === 'front' ? 'white' : '#1a7a4a',
                  fontWeight: 600,
                }}
              >Front</button>
            )}
            {hasBack && (
              <button
                onClick={() => setSide('back')}
                style={{
                  padding: '5px 14px', fontSize: 13, borderRadius: 5, cursor: 'pointer',
                  border: '1.5px solid #6c757d',
                  background: side === 'back' ? '#6c757d' : 'white',
                  color: side === 'back' ? 'white' : '#6c757d',
                  fontWeight: 600,
                }}
              >Back</button>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '5px 12px', fontSize: 16, borderRadius: 5, cursor: 'pointer',
                border: '1.5px solid #dee2e6', background: '#f8f9fa', color: '#495057',
                fontWeight: 700, marginLeft: 4,
              }}
              title="Close"
            >✕</button>
          </div>
        </div>

        {/* Image */}
        <div style={{ flex: 1, overflow: 'auto', textAlign: 'center', background: '#f1f3f5', borderRadius: 8, padding: 10 }}>
          {cheque.checkImages?.[side] ? (
            <img
              key={side}
              src={imgUrl(cheque.checkImages[side])}
              alt={`Check ${side}`}
              style={{
                maxWidth: '100%',
                maxHeight: '72vh',
                borderRadius: 6,
                objectFit: 'contain',
                boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
              }}
            />
          ) : (
            <div style={{ padding: 40, color: '#6c757d', fontSize: 14 }}>
              No {side} image attached.
            </div>
          )}
        </div>

        {/* Footer label */}
        <div style={{ textAlign: 'center', fontSize: 11, color: '#adb5bd' }}>
          Click outside or ✕ to close
        </div>
      </div>
    </div>
  );
}

/**
 * ChequeDetail page component.
 * @component
 * @returns {React.ReactElement}
 */
export default function ChequeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cheque, setCheque] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showChequeModal, setShowChequeModal] = useState(false);

  useEffect(() => {
    getCheque(id).then(res => {
      setCheque(res.data.data);
      setForm(res.data.data);
    }).catch(() => toast.error('Failed to load transaction')).finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await modifyCheque(id, {
        checkNumber: form.checkNumber,
        checkDate: form.checkDate,
        dateCashed: form.cashedOn || form.dateCashed,
        checkType: form.checkType,
        amount: parseFloat(form.amount),
        fee: parseFloat(form.fee),
        maker: form.maker,
        status: form.status,
        notes: form.notes,
      });
      setCheque(res.data.data);
      setEditMode(false);
      toast.success('Transaction updated!');
    } catch { toast.error('Update failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <Layout title="Transaction"><div className="loading-screen"><div className="spinner spinner-dark" /></div></Layout>;
  if (!cheque) return <Layout title="Transaction"><div className="empty-state"><h3>Transaction not found</h3></div></Layout>;

  const c = editMode ? form : cheque;
  void c;

  return (
    <Layout title="Transaction Detail">
      <div className="flex items-center gap-3 mb-4">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/cheques')}>← Back</button>
        <span style={{ color: 'var(--gray-2)' }}>/</span>
        <span style={{ fontSize: 13, color: 'var(--gray-1)' }}>Check #{cheque.checkNumber}</span>
      </div>

      {/* Header */}
      <div className="card mb-4">
        <div className="card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800 }}>
                  Check #{cheque.checkNumber}
                </h1>
                <span className={`badge ${statusColor[cheque.status] || 'badge-gray'}`}>{cheque.status}</span>
                {cheque.isLoan && <span className="badge badge-red">🔴 LOAN</span>}
              </div>
              <div style={{ color: 'var(--gray-1)', fontSize: 13 }}>
                {cheque.checkType} · Cashed on {formatDateLong(cheque.dateCashed)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {editMode ? (
                <>
                  <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                    {saving ? <span className="spinner" /> : '💾 Save Changes'}
                  </button>
                  <button className="btn btn-outline" onClick={() => { setEditMode(false); setForm(cheque); }}>Cancel</button>
                </>
              ) : (
                <>
                  <button
                    className="btn btn-outline"
                    onClick={() => setShowChequeModal(true)}
                    disabled={!cheque.checkImages?.front && !cheque.checkImages?.back}
                    title={(!cheque.checkImages?.front && !cheque.checkImages?.back) ? 'No cheque images attached' : 'View cheque images'}
                  >
                    🖼️ Display Cheque
                  </button>
                  <button className="btn btn-outline" onClick={() => setEditMode(true)}>✏️ Modify Check</button>
                  <button className="btn btn-primary" onClick={() => window.print()}>🖨️ Print Validation</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="cheque-detail-grid" style={{ display: 'grid', gap: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Transaction amounts */}
          <div className="card">
            <div className="card-header" style={{ background: 'var(--dark)', borderRadius: '12px 12px 0 0' }}>
              <h3 style={{ color: 'white' }}>💵 Transaction Amounts</h3>
            </div>
            <div className="card-body">
              <div className="amounts-3col" style={{ display: 'grid', gap: 16 }}>
                {[
                  { label: 'Check Amount', val: fmt(cheque.amount), color: 'var(--dark)' },
                  { label: 'Fee Charged', val: fmt(cheque.fee), color: 'var(--danger)' },
                  { label: 'Customer Payout', val: fmt(cheque.payOut), color: 'var(--success)' },
                ].map(s => (
                  <div key={s.label} style={{ background: 'var(--gray-4)', borderRadius: 10, padding: '16px', textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: 'var(--gray-1)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{s.label}</div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 900, color: s.color }}>{s.val}</div>
                  </div>
                ))}
              </div>
              {editMode && (
                <div className="form-row mt-4">
                  <div className="form-group">
                    <label className="form-label">Amount ($)</label>
                    <input className="form-control" type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fee ($)</label>
                    <input className="form-control" type="number" step="0.01" value={form.fee} onChange={e => setForm(f => ({ ...f, fee: e.target.value }))} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Check details */}
          <div className="card">
            <div className="card-header"><h3>📝 Check Details</h3></div>
            <div className="card-body">
              <div className="form-row">
                {[
                  { label: 'Check Number', key: 'checkNumber', type: 'text' },
                  { label: 'Maker', key: 'maker', type: 'text' },
                  { label: 'Check Type', key: 'checkType', type: 'select', opts: ['Personal', 'Business', 'Government', 'Payroll', 'Insurance', 'Money Order', 'Other'] },
                  { label: 'Status', key: 'status', type: 'select', opts: ['Pending', 'Cashed', 'Returned', 'Hold', 'Void'] },
                ].map(field => (
                  <div className="form-group" key={field.key}>
                    <label className="form-label">{field.label}</label>
                    {editMode ? (
                      field.type === 'select'
                        ? <select className="form-control form-select" value={form[field.key] || ''} onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))}>
                            {field.opts.map(o => <option key={o}>{o}</option>)}
                          </select>
                        : <input className="form-control" type={field.type} value={form[field.key] || ''} onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))} />
                    ) : (
                      <div style={{ padding: '10px 14px', background: 'var(--gray-4)', borderRadius: 'var(--radius-sm)', fontSize: 13.5, fontWeight: 600 }}>
                        {cheque[field.key] || '—'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Date fields row */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date on Check</label>
                  {editMode ? (
                    <DateInput
                      className="form-control"
                      value={form.checkDate?.split('T')[0] || ''}
                      onChange={e => setForm(f => ({ ...f, checkDate: e.target.value }))}
                    />
                  ) : (
                    <div style={{ padding: '10px 14px', background: 'var(--gray-4)', borderRadius: 'var(--radius-sm)', fontSize: 13.5, fontWeight: 600 }}>
                      {formatDate(cheque.checkDate)}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Cashed On (Transaction Date)</label>
                  {editMode ? (
                    <DateInput
                      className="form-control"
                      value={form.cashedOn || form.dateCashed?.split('T')[0] || ''}
                      onChange={e => setForm(f => ({ ...f, cashedOn: e.target.value }))}
                    />
                  ) : (
                    <div style={{ padding: '10px 14px', background: 'var(--gray-4)', borderRadius: 'var(--radius-sm)', fontSize: 13.5, fontWeight: 600 }}>
                      {formatDate(cheque.dateCashed)}
                    </div>
                  )}
                </div>
              </div>
              {editMode && (
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" rows={3} value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
              )}
              {!editMode && cheque.notes && (
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <div style={{ padding: '10px 14px', background: 'var(--gray-4)', borderRadius: 'var(--radius-sm)', fontSize: 13.5 }}>{cheque.notes}</div>
                </div>
              )}
            </div>
          </div>

          {/* Bank info */}
          {cheque.bank?.name && (
            <div className="card">
              <div className="card-header"><h3>🏦 Bank Information</h3></div>
              <div className="card-body">
                <div className="form-row">
                  {[['Bank', cheque.bank.name], ['Routing #', cheque.bank.routingNumber], ['Account #', cheque.bank.accountNumber]].map(([l, v]) => v && (
                    <div key={l} className="form-group">
                      <label className="form-label">{l}</label>
                      <div style={{ padding: '10px 14px', background: 'var(--gray-4)', borderRadius: 'var(--radius-sm)', fontFamily: 'monospace' }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Customer */}
          <div className="card">
            <div className="card-header"><h3>👤 Customer</h3></div>
            <div className="card-body">
              {cheque.customer ? (
                <>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{cheque.customer.firstName} {cheque.customer.lastName}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--gray-1)', marginTop: 4 }}>
                    {cheque.customer.address?.city}, {cheque.customer.address?.state}
                  </div>
                  <button className="btn btn-outline btn-sm mt-2 w-full" onClick={() => navigate(`/customers/${cheque.customer._id}`)}>View Profile →</button>
                </>
              ) : <span className="text-muted">Customer not found</span>}
            </div>
          </div>

          {/* Verify */}
          {cheque.verifyCode?.grade && (
            <div className="card">
              <div className="card-header"><h3>✅ Verification</h3></div>
              <div className="card-body">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: 'var(--gray-1)', fontWeight: 600, textTransform: 'uppercase' }}>Verify Grade</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 900, color: 'var(--primary)' }}>{cheque.verifyCode.grade}/10</div>
                </div>
              </div>
            </div>
          )}

          {/* Meta */}
          <div className="card">
            <div className="card-header"><h3>ℹ️ Record Info</h3></div>
            <div className="card-body" style={{ fontSize: 12.5 }}>
              {[
                ['Processed By', cheque.processedBy?.name || '—'],
                ['Created At', formatDateTime(cheque.createdAt)],
                ['Last Modified', cheque.modifiedAt ? formatDateTime(cheque.modifiedAt) : '—'],
                ['Modified By', cheque.modifiedBy?.name || '—'],
              ].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--gray-4)' }}>
                  <span style={{ color: 'var(--gray-1)' }}>{l}</span>
                  <strong>{v}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showChequeModal && (
        <ChequeImageModal
          cheque={cheque}
          onClose={() => setShowChequeModal(false)}
        />
      )}
    </Layout>
  );
}
